// Seeded gradient (Perlin-style) noise. Pure, no Pixi, unit-testable.
import { mulberry32 } from "../lib/rng";

export type Noise1D = (x: number) => number;
export type Noise2D = (x: number, y: number) => number;

function permutation(seed: number): Uint8Array {
  const rand = mulberry32(seed);
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const t = p[i];
    p[i] = p[j];
    p[j] = t;
  }
  const perm = new Uint8Array(512);
  for (let i = 0; i < 512; i++) perm[i] = p[i & 255];
  return perm;
}

const fade = (t: number) => t * t * t * (t * (t * 6 - 15) + 10);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

// 8 evenly spaced unit gradient directions.
const GX = new Float64Array(8);
const GY = new Float64Array(8);
for (let i = 0; i < 8; i++) {
  GX[i] = Math.cos((i / 8) * Math.PI * 2);
  GY[i] = Math.sin((i / 8) * Math.PI * 2);
}

/** 2D gradient noise in [-1, 1], deterministic for a given seed. */
export function createNoise2D(seed: number): Noise2D {
  const perm = permutation(seed);
  return (x: number, y: number): number => {
    const xf0 = Math.floor(x);
    const yf0 = Math.floor(y);
    const X = xf0 & 255;
    const Y = yf0 & 255;
    const xf = x - xf0;
    const yf = y - yf0;
    const u = fade(xf);
    const v = fade(yf);
    const aa = perm[perm[X] + Y] & 7;
    const ab = perm[perm[X] + Y + 1] & 7;
    const ba = perm[perm[X + 1] + Y] & 7;
    const bb = perm[perm[X + 1] + Y + 1] & 7;
    const n00 = GX[aa] * xf + GY[aa] * yf;
    const n10 = GX[ba] * (xf - 1) + GY[ba] * yf;
    const n01 = GX[ab] * xf + GY[ab] * (yf - 1);
    const n11 = GX[bb] * (xf - 1) + GY[bb] * (yf - 1);
    // Max magnitude for unit gradients is sqrt(2)/2; scale to roughly [-1,1].
    const r = lerp(lerp(n00, n10, u), lerp(n01, n11, u), v) * 1.4142;
    return r > 1 ? 1 : r < -1 ? -1 : r;
  };
}

/** 1D gradient noise in [-1, 1]. */
export function createNoise1D(seed: number): Noise1D {
  const perm = permutation(seed ^ 0x5bd1e995);
  const grad = new Float32Array(256);
  const rand = mulberry32(seed + 17);
  for (let i = 0; i < 256; i++) grad[i] = rand() * 2 - 1;
  return (x: number): number => {
    const x0 = Math.floor(x);
    const t = x - x0;
    const g0 = grad[perm[x0 & 255]];
    const g1 = grad[perm[(x0 + 1) & 255]];
    const r = lerp(g0 * t, g1 * (t - 1), fade(t)) * 2;
    return r > 1 ? 1 : r < -1 ? -1 : r;
  };
}

/** Fractal sum of 1D noise, normalised to [-1, 1]. */
export function fbm1D(noise: Noise1D, x: number, octaves = 4, lacunarity = 2, gain = 0.5): number {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let f = 1;
  for (let i = 0; i < octaves; i++) {
    sum += noise(x * f + i * 31.7) * amp;
    norm += amp;
    amp *= gain;
    f *= lacunarity;
  }
  return sum / norm;
}

/** Fractal sum of 2D noise, normalised to [-1, 1]. */
export function fbm2D(noise: Noise2D, x: number, y: number, octaves = 4, lacunarity = 2, gain = 0.5): number {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let f = 1;
  for (let i = 0; i < octaves; i++) {
    sum += noise(x * f + i * 19.1, y * f - i * 7.3) * amp;
    norm += amp;
    amp *= gain;
    f *= lacunarity;
  }
  return sum / norm;
}
